/*
 * PaidHumanRecommendedPill — soft "Recommended: Solution partner" box.
 *
 * Fires when the Suggestion Generator on the backend computes
 * suggestion.approach='paid_human' via any of its three rules:
 *   rule_1_regulated  — regulated industry + complex org
 *   rule_2_burned     — prior AI investment didn't pay off
 *   rule_3_user_asked — user picked Done-for-me on help_level
 *
 * Sits above the chat as a small pill — non-interruptive. Click expands
 * the full PaidHumanCard inline (parent pushes a synthetic orbit turn
 * carrying the offer_paid_human directive). The card itself owns the
 * mock slot picker + booking flow.
 *
 * Distinct from the HARD path: scoping_difficulty ≥ 2 auto-injects the
 * card directly mid-conversation. This pill is for the calmer case
 * where signals point to paid_human but the user isn't actively stuck.
 */

import React from 'react';
import { ChevronRight, Users } from 'lucide-react';

import type { SuggestionResult } from '../lib/dossier-types';


export interface PaidHumanRecommendedPillProps {
  suggestion: SuggestionResult | null;
  /** Already booked this engagement? Don't show the recommendation. */
  alreadyBooked?: boolean;
  /** True once the user clicked the pill or the hard card was shown
   *  this session — don't re-show the pill after that. */
  alreadyOpened?: boolean;
  onOpen: () => void;
}


export const PaidHumanRecommendedPill: React.FC<PaidHumanRecommendedPillProps> = ({
  suggestion, alreadyBooked, alreadyOpened, onOpen,
}) => {
  if (!suggestion) return null;
  if (suggestion.approach !== 'paid_human') return null;
  if (alreadyBooked) return null;
  if (alreadyOpened) return null;

  // Title pulled from the rule. Each maps to a short reason hint so
  // the user sees WHY paid-human came up instead of "we recommend it".
  const ruleHints: Record<string, string> = {
    rule_1_regulated:  'regulated industry + complex org',
    rule_2_burned:     'last AI investment didn’t pay off',
    rule_3_user_asked: 'you said done-for-me',
  };
  const ruleHint = ruleHints[suggestion.rule] || '';
  const body = suggestion.suggestion_line || 'A Solution partner can scope this with you.';

  return (
    <button
      type="button"
      className="aa-ph-pill"
      onClick={onOpen}
      aria-label={`Open Solution partner recommendation — ${ruleHint || body}`}
    >
      <span className="aa-ph-pill-icon" aria-hidden="true">
        <Users size={13} strokeWidth={2.25} />
      </span>
      <span className="aa-ph-pill-eyebrow">Recommended</span>
      <span className="aa-ph-pill-text">
        Solution partner — <em>{ruleHint || body}</em>
      </span>
      <span className="aa-ph-pill-cta">
        See times
        <ChevronRight size={12} strokeWidth={2.5} />
      </span>
    </button>
  );
};

export default PaidHumanRecommendedPill;
